import React, { useEffect, useState, useRef, useCallback } from 'react';
import './Puzzle.css';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import SolutionRenderer from './components/SolutionRenderer';
import { convertToJSON } from './utils/puzzleUtils';
import { puzzleAPI } from './services/api';

const colorMapping = {
  red: 'r',
  blue: 'b',
  yellow: 'y',
  black: 'x',
};

const Puzzle = () => {
  const navigate = useNavigate();
  const { boardNumber } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [gridSize, setGridSize] = useState(8);
  const [grid, setPuzzleGrid] = useState(() => 
    new Array(8).fill(null).map(() => new Array(8).fill({ color: 'black', counter: 0 }))
  );
  const [puzzleName, setName] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [funFact, setFunFact] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [bookletNumber, setBookletNumber] = useState('');
  const COLORS = ['red', 'blue', 'yellow', 'black'];
  const [highlightedPiece, setHighlightedPiece] = useState(null);
  const [isSolving, setIsSolving] = useState(false);
  const [solutionData, setSolutionData] = useState(null);
  const [renderedSolutions, setRenderedSolutions] = useState([]);

  const location = useLocation();
  const puzzleData = location.state?.puzzleData;

  const gridContainerRef = useRef(null);

  const fetchPuzzleData = useCallback(async () => {
    try {
      const puzzle = await puzzleAPI.getPuzzle(boardNumber);
      const patternLength = puzzle.pattern.length;
      setGridSize(patternLength);
      setName(puzzle.name);
      setDescription(puzzle.description);
      setDifficulty(puzzle.book_difficulty);
      setFunFact(puzzle.fun_fact);
      setSerialNo(puzzle.board_number);
      setBookletNumber(puzzle.booklet_number != null ? String(puzzle.booklet_number) : '');

      const preFilledGrid = puzzle.pattern.map(row =>
        row.map(code => ({
          color: Object.keys(colorMapping).find(key => colorMapping[key] === code),
          counter: 0
        }))
      );
      setPuzzleGrid(preFilledGrid);
      setIsLoading(false);
    } catch (err) {
      console.error("Failed to fetch puzzle data:", err);
      setError("Failed to load puzzle data.");
      setIsLoading(false);
    }
  }, [boardNumber]);

  const initializeGrid = useCallback((size) => {
    const newGrid = new Array(size).fill(null).map(() => 
      new Array(size).fill({ color: 'black', counter: 0 })
    );
    setPuzzleGrid(newGrid);
  }, []); // Empty dependency array since it only uses size parameter

  useEffect(() => {
    if (puzzleData) {
      setGridSize(puzzleData.gridSize);
      setPuzzleGrid(puzzleData.grid);
      setIsLoading(false);
    } else if (boardNumber) {
      fetchPuzzleData();
    } else {
      initializeGrid(8); // Use fixed initial size
      setIsLoading(false);
    }
  }, [puzzleData, boardNumber, fetchPuzzleData, initializeGrid]);
  
  // Second effect for handling grid size changes
  useEffect(() => {
    if (!puzzleData && !boardNumber) {
      initializeGrid(gridSize);
    }
  }, [gridSize, boardNumber, puzzleData, initializeGrid]);

  const handleTrySolve = async () => {
    setIsSolving(true);
    setError('');
    setSolutionData(null);
    setRenderedSolutions([]);

    const transposeGrid = grid[0].map((_, colIndex) => grid.map(row => row[colIndex]));

    const puzzleData = convertToJSON(puzzleName, serialNo, difficulty, description, funFact, transposeGrid, bookletNumber);

    try {
      const data = await puzzleAPI.solvePuzzle(puzzleData.Pattern);
      setSolutionData(data);
      if (data.rendered_solutions) {
        setRenderedSolutions(data.rendered_solutions);
      } else {
        setRenderedSolutions([]);
      }
    } catch (error) {
      console.error('Error solving puzzle:', error);
      setError(error.response?.data?.message || 'Failed to solve puzzle');
    } finally {
      setIsSolving(false);
    }
  };

  const handleSubmit = async () => {
    if (gridSize === 8 && bookletNumber.trim() === '') {
      window.alert('Please enter a booklet number. 8x8 puzzles require one.');
      return;
    }
    setIsSubmitting(true);
    const transposeGrid = grid[0].map((_, colIndex) => grid.map(row => row[colIndex]));
    console.log(transposeGrid);
    const puzzleData = convertToJSON(puzzleName, serialNo, difficulty, description, funFact, transposeGrid, bookletNumber);
    try {
      const data = await puzzleAPI.createOrUpdatePuzzle(puzzleData, boardNumber);
      console.log(data);
      window.alert(`Puzzle ${boardNumber ? 'updated' : 'created'} successfully! 
        Number of solutions: ${data.num_solutions},
        Computer difficulty: ${data.difficulty}`);

      // If we're updating an existing puzzle, just navigate to the same puzzle page
      // If we're creating a new puzzle, navigate to the new puzzle page
      if (boardNumber) {
        navigate(`/puzzle/${boardNumber}`);
      } else if (data.board_number) {
        navigate(`/puzzle/${data.board_number}`);
      } else {
        // Fallback if no board number is returned
        navigate('/');
      }
      
      // Reset the submitting state
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error:', error);
      setIsSubmitting(false);
      window.alert(`Failed to submit puzzle: ${error.response?.data?.message || 'Unknown error'}`);
    }
  };

  const updateColorAndCounter = (row, col) => {
    setPuzzleGrid(prevGrid => prevGrid.map((rowArr, rowIndex) =>
      rowIndex === row
        ? rowArr.map((cell, colIndex) =>
          colIndex === col
            ? {
              ...cell,
              color: COLORS[(COLORS.indexOf(cell.color) + 1) % COLORS.length],
              counter: cell.counter + 1
            }
            : cell
        )
        : rowArr
    ));
  };

  const handleClearGrid = () => {
    initializeGrid(gridSize);
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="challenge-container">
      <input
        type="text"
        value={puzzleName}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name of Challenge"
        className="input-field"
      />
      <input
        type="text"
        value={serialNo}
        onChange={(e) => setSerialNo(e.target.value)}
        placeholder="Serial Number"
        className="input-field"
        readOnly={boardNumber ? true : false} // Make it read-only if we're editing an existing puzzle
      />
      {gridSize === 8 && (
        <select
          value={bookletNumber}
          onChange={(e) => setBookletNumber(e.target.value)}
          className="select-field"
        >
          <option value="">Booklet Number</option>
          <option value="1">Booklet 1</option>
          <option value="2">Booklet 2</option>
        </select>
      )}
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description"
        className="textarea-field"
        maxLength={100}
      />
      <textarea
        value={funFact}
        onChange={(e) => setFunFact(e.target.value)}
        placeholder="Fun Fact"
        className="textarea-field"
        maxLength={100}
      />
      <select
        value={difficulty}
        onChange={(e) => setDifficulty(e.target.value)}
        className="select-field"
      >
        <option value="">Book Difficulty</option>
        <option value="easy">Easy</option>
        <option value="medium">Medium</option>
        <option value="hard">Hard</option>
        <option value="unrated">Unrated</option>
      </select>
      <button
        className="try-solve-button"
        onClick={handleTrySolve}
        disabled={isSolving}
      >
        {isSolving ? <div className="loader"></div> : 'Solve'}
      </button>
      <div className="grid-container">
        <div
          className={`grid grid-${gridSize}x${gridSize}`}
          id="gc"
          ref={gridContainerRef}
        >
          {grid.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              {row.map((cell, colIndex) => (
                <div
                  key={colIndex}
                  className="grid-cell"
                  style={{ backgroundColor: cell.color }}
                  onClick={() => updateColorAndCounter(rowIndex, colIndex)}
                ></div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
      <button
        className="clear-button"
        onClick={handleClearGrid}
      >
        Clear Grid
      </button>
      <button
        className="confirm-button"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? <div className="loader"></div> : 'Confirm'}
      </button>

      {solutionData && (
        <div className="solution-info">
          <h3>Solution Information</h3>
          <p>Number of solutions: {solutionData.num_solutions}</p>
          <div className="solutions-container">
            {renderedSolutions.map((solution, index) => (
              <SolutionRenderer
                key={index}
                solution={solution}
                gridSize={gridSize}
                highlightedPiece={highlightedPiece}
                setHighlightedPiece={setHighlightedPiece}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Puzzle;
